// Copyright (C) 2023 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col } from 'antd/lib/grid';
import { CombinedState } from 'reducers';
import Text from 'antd/lib/typography/Text';
import Slider from 'antd/lib/slider';

import {
    enableImageFilter,
    disableImageFilter,
} from 'actions/settings-actions';
import GammaCorrection from 'utils/fabric-wrapper/gamma-correciton';
import { ImageFilterAlias, hasFilter } from 'utils/image-processing';
import { updateStoredColorSettings, getStoredColorSettings } from 'utils/color-settings-storage';

const defaultGamma = 1;
const gammaMin = 0.2;
const gammaMax = 2.6;
const gammaStep = 0.01;

export default function GammaFilter(): JSX.Element {
    const dispatch = useDispatch();
    const [gamma, setGamma] = useState<number>(defaultGamma);
    const filters = useSelector((state: CombinedState) => state.settings.imageFilters);
    const gammaFilter = hasFilter(filters, ImageFilterAlias.GAMMA_CORRECTION);
    const jobId = useSelector((state: CombinedState) => state.annotation.job.instance?.id);
    const frameNumber = useSelector((state: CombinedState) => state.annotation.player.frame.number);

    const onChangeGamma = useCallback((newGamma: number): void => {
        const normalizedGamma = Math.min(Math.max(newGamma, gammaMin), gammaMax);
        setGamma(normalizedGamma);
        updateStoredColorSettings({ jobId, frame: frameNumber }, {
            gamma: normalizedGamma === defaultGamma ? undefined : normalizedGamma,
        });
        if (normalizedGamma === defaultGamma) {
            if (gammaFilter) {
                dispatch(disableImageFilter(ImageFilterAlias.GAMMA_CORRECTION));
            }
        } else {
            const convertedGamma = [normalizedGamma, normalizedGamma, normalizedGamma];
            if (gammaFilter) {
                dispatch(enableImageFilter(gammaFilter, { gamma: convertedGamma }));
            } else {
                dispatch(enableImageFilter({
                    modifier: new GammaCorrection({ gamma: convertedGamma }),
                    alias: ImageFilterAlias.GAMMA_CORRECTION,
                }));
            }
        }
    }, [gammaFilter, dispatch, jobId, frameNumber]);

    useEffect(() => {
        if (!jobId) {
            return;
        }

        if (filters.length === 0) {
            setGamma(defaultGamma);
            updateStoredColorSettings({ jobId, frame: frameNumber }, { gamma: undefined });
        } else if (gammaFilter) {
            const filterGamma = (gammaFilter.modifier as GammaCorrection).gamma;
            setGamma(filterGamma);
            updateStoredColorSettings({ jobId, frame: frameNumber }, {
                gamma: filterGamma === defaultGamma ? undefined : filterGamma,
            });
        }
    }, [filters, gammaFilter, jobId, frameNumber]);

    useEffect(() => {
        if (!jobId) {
            return;
        }

        const stored = getStoredColorSettings({ jobId, frame: frameNumber });
        const storedGamma = stored.gamma ?? defaultGamma;
        if (storedGamma !== gamma) {
            onChangeGamma(storedGamma);
        }
    }, [jobId, frameNumber]);

    return (
        <div className='cvat-image-setups-filters'>
            <Row justify='space-around'>
                <Col span={24}>
                    <Row className='cvat-image-setups-gamma'>
                        <Col span={6}>
                            <Text className='cvat-text-color'> Gamma </Text>
                        </Col>
                        <Col span={12}>
                            <Slider
                                min={gammaMin}
                                max={gammaMax}
                                value={gamma}
                                step={gammaStep}
                                onChange={onChangeGamma}
                            />
                        </Col>
                    </Row>
                </Col>
            </Row>
        </div>
    );
}
